import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';
import {
  PageHeader,
  PageFilters,
  DataTable,
  TablePaginationBar,
  StatusBadge,
  ActionMenu,
} from '../../components/ui';
import type { Column, ActionItem } from '../../components/ui';
import { jobsApi } from '../../api/jobs';
import { PAGE_SIZE, SERVICE_CATEGORIES } from '../../constants';
import { formatDate, paginate, matchesSearch } from '../../utils';
import type { Job, JobStatus } from '../../types';

const STATUS_OPTIONS: JobStatus[] = [
  'Assigned',
  'Accepted',
  'In Progress',
  'Completed',
  'Cancelled',
];

const JobsPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await jobsApi.getJobs({ page: 1, limit: 1000 });
      setRows(data.jobs.filter((j) => j.status !== 'Pending'));
    } catch {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      const q = search.trim();
      const matchesQ =
        !q ||
        matchesSearch(row.id, q) ||
        matchesSearch(row.customerName, q) ||
        matchesSearch(row.category, q) ||
        matchesSearch(row.providerName || '', q);
      const matchesCategory = category === 'all' || row.category === category;
      const matchesStatus = status === 'all' || row.status === status;
      return matchesQ && matchesCategory && matchesStatus;
    });
  }, [rows, search, category, status]);

  const paged = useMemo(() => paginate(filtered, page, PAGE_SIZE), [filtered, page]);

  useEffect(() => {
    setPage(1);
  }, [search, category, status]);

  const columns: Column<Job>[] = [
    {
      id: 'id',
      label: 'ID',
      minWidth: 110,
      render: (row) => (
        <Typography
          fontWeight={600}
          fontSize={13}
          sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
          onClick={() => navigate(`/jobs/${row.id}`)}
        >
          {row.id.replace('REQ', 'JOB')}
        </Typography>
      ),
    },
    {
      id: 'customer',
      label: 'Customer',
      minWidth: 140,
      render: (row) => row.customerName,
    },
    {
      id: 'category',
      label: 'Category',
      render: (row) => row.category,
    },
    {
      id: 'provider',
      label: 'Assigned Provider',
      minWidth: 150,
      render: (row) => row.providerName || '—',
    },
    {
      id: 'scheduled',
      label: 'Scheduled Date',
      render: (row) => formatDate(row.scheduledDate),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      id: 'payment',
      label: 'Payment Status',
      render: (row) => <StatusBadge status={row.paymentStatus} />,
    },
    {
      id: 'created',
      label: 'Created',
      render: (row) => formatDate(row.createdAt),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: (row) => {
        const actions: ActionItem[] = [
          {
            label: 'View',
            icon: <VisibilityIcon fontSize="small" />,
            onClick: () => navigate(`/jobs/${row.id}`),
          },
        ];
        return <ActionMenu actions={actions} />;
      },
    },
  ];

  return (
    <Box>
      <PageHeader title="Jobs" subtitle="Track assigned and in-progress service jobs" />

      <PageFilters
        search={search}
        searchPlaceholder="Search ID, customer, provider…"
        onSearchChange={setSearch}
        filters={[
          {
            id: 'category',
            label: 'Category',
            value: category,
            onChange: setCategory,
            options: [
              { label: 'All Categories', value: 'all' },
              ...SERVICE_CATEGORIES.map((c) => ({ label: c, value: c })),
            ],
          },
          {
            id: 'status',
            label: 'Status',
            value: status,
            onChange: setStatus,
            options: [
              { label: 'All Statuses', value: 'all' },
              ...STATUS_OPTIONS.map((s) => ({ label: s, value: s })),
            ],
          },
        ]}
      />

      <DataTable
        columns={columns}
        rows={paged}
        loading={loading}
        getRowId={(row) => row.id}
        emptyTitle="No jobs found"
        emptyDescription="Try adjusting your search or filters."
      />

      <TablePaginationBar
        page={page}
        pageSize={PAGE_SIZE}
        total={filtered.length}
        onChange={setPage}
      />
    </Box>
  );
};

export default JobsPage;
